import firebase from "./firebase";
import app from "./restApp";

const DEFAULT_LINK = "/";
const FIREBASE_MESSAGING_SW_PATH = "/firebase-messaging-sw.js";
const FIREBASE_MESSAGING_SW_SCOPE = "/firebase-cloud-messaging-push-scope";

export const getPayloadTitle = (payload) =>
  payload?.notification?.title || payload?.data?.notificationTitle || "TGOne";

export const getPayloadBody = (payload) =>
  payload?.notification?.body || payload?.data?.notificationBody || "";

export function getPayloadLink(payload) {
  const link = (
    payload?.data?.link ||
    payload?.fcmOptions?.link ||
    payload?.notification?.click_action ||
    DEFAULT_LINK
  );

  const occurrenceStart = payload?.data?.occurrenceStart;
  if (!occurrenceStart) return link;

  try {
    const origin = typeof window !== "undefined"
      ? window.location.origin
      : "http://localhost";
    const target = new URL(link, origin);
    if (target.pathname !== "/calendar") return link;

    const occurrenceDate = new Date(occurrenceStart);
    if (!Number.isNaN(occurrenceDate.valueOf()) && !target.searchParams.has("date")) {
      const localDate = new Date(
        occurrenceDate.valueOf() - occurrenceDate.getTimezoneOffset() * 60000,
      ).toISOString().slice(0, 10);
      target.searchParams.set("date", localDate);
    }
    if (!target.searchParams.has("view")) target.searchParams.set("view", "agenda");

    return target.origin === origin
      ? `${target.pathname}${target.search}${target.hash}`
      : target.href;
  } catch (_error) {
    return link;
  }
}

export function resolveNotificationLink(rawLink = DEFAULT_LINK) {
  const link = typeof rawLink === "string" && rawLink.trim()
    ? rawLink.trim()
    : DEFAULT_LINK;

  try {
    return new URL(link, window.location.origin).href;
  } catch (_error) {
    return new URL(DEFAULT_LINK, window.location.origin).href;
  }
}

export function openNotificationLink(rawLink) {
  if (typeof window === "undefined") return;
  window.location.assign(resolveNotificationLink(rawLink));
}

async function getTokenOptions() {
  const serviceWorkerRegistration = await navigator.serviceWorker.register(
    FIREBASE_MESSAGING_SW_PATH,
    { scope: FIREBASE_MESSAGING_SW_SCOPE },
  );
  serviceWorkerRegistration.update?.().catch?.(() => {});

  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  return {
    ...(vapidKey ? { vapidKey } : {}),
    serviceWorkerRegistration,
  };
}

function isMessagingSupported() {
  try {
    if (typeof window === "undefined") return false;
    if (!("Notification" in window)) return false;
    if (!("serviceWorker" in navigator)) return false;
    if (firebase.messaging?.isSupported && !firebase.messaging.isSupported()) return false;
    return true;
  } catch (_error) {
    return false;
  }
}

export async function requestAndSyncPushToken(user) {
  if (!user?.id) {
    return { ok: false, reason: "missing-user" };
  }

  if (!isMessagingSupported()) {
    return { ok: false, reason: "unsupported" };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { ok: false, reason: "permission-denied", permission };
    }

    const messaging = firebase.messaging();
    const tokenPush = await messaging.getToken(await getTokenOptions());

    if (!tokenPush) {
      return { ok: false, reason: "empty-token" };
    }

    if (tokenPush === user?.vc_push) {
      return { ok: true, token: tokenPush, patched: false };
    }

    await app.service("usuarios").patch(user.id, { vc_push: tokenPush });

    return { ok: true, token: tokenPush, patched: true };
  } catch (error) {
    console.error("Error al sincronizar token push:", error);
    return { ok: false, reason: "sync-error", error };
  }
}

export function showForegroundNotification(payload) {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window)) return false;
  if (Notification.permission !== "granted") return false;

  const link = getPayloadLink(payload);
  const title = getPayloadTitle(payload);
  const notification = new Notification(title, {
    body: getPayloadBody(payload),
    icon: "/logo192.png",
    data: { link }
  });

  notification.onclick = () => {
    window.focus();
    openNotificationLink(link);
    notification.close();
  };

  return true;
}

export function registerForegroundPushHandler(onPayload) {
  if (!isMessagingSupported()) return () => {};

  try {
    const messaging = firebase.messaging();
    const unsubscribe = messaging.onMessage((payload) => {
      console.log('messages ------>', payload)
      showForegroundNotification(payload);
      if (onPayload) onPayload(payload);
    });

    return typeof unsubscribe === "function" ? unsubscribe : () => {};
  } catch (error) {
    console.warn("Foreground push handler skipped:", error?.message || error);
    return () => {};
  }
}
